import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Share2, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Shown to finishers: a personal link to gift a colleague the path.
export default function ReferralGift({ variant = "light" }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  const message = `I just finished How Truth Travels — the craft of receiving difficult truth. Walk the path with me: ${data.link}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast.success("Your gift link is copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy — select the link and copy it manually.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "How Truth Travels", text: message, url: data.link }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    copy();
  };

  const dark = variant === "dark";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`rounded-2xl border p-7 sm:p-8 ${dark ? "bg-purple-deep/40 border-gold/30" : "bg-white border-gold/50"}`}
      data-testid="referral-gift"
    >
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${dark ? "bg-gold/15" : "bg-gold/12"}`}>
          <Gift className={`w-6 h-6 ${dark ? "text-gold-pale" : "text-gold-dark"}`} />
        </div>
        <div className="min-w-0">
          <h3 className={`font-heading text-2xl ${dark ? "text-white" : "text-purple"}`}>Gift a colleague the path</h3>
          <p className={`text-[14px] mt-1 leading-relaxed ${dark ? "text-white/70" : "text-ink/70"}`}>
            You walked it — now open the door for someone else. Share your personal link and bring a colleague aboard.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <input
              readOnly
              value={data.link}
              onFocus={(e) => e.target.select()}
              className={`flex-1 min-w-0 rounded-full px-5 py-2.5 text-[13px] outline-none ${dark ? "bg-purple-deep/60 border border-white/15 text-white/90" : "bg-cream/60 border border-line text-ink"}`}
              data-testid="referral-link-input"
            />
            <div className="flex gap-2.5 shrink-0">
              <button onClick={copy} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-bold transition-colors ${dark ? "border border-gold/50 text-gold-pale hover:bg-gold hover:text-purple-deep" : "border border-purple/40 text-purple hover:bg-wash"}`} data-testid="referral-copy-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
              </button>
              <button onClick={share} className="inline-flex items-center gap-2 bg-gold text-purple-deep font-bold rounded-full px-5 py-2.5 text-[14px] hover:bg-gold-pale transition-colors" data-testid="referral-share-btn">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>

          {data.invited > 0 && (
            <p className={`mt-4 inline-flex items-center gap-2 text-[13px] font-semibold ${dark ? "text-gold-pale" : "text-gold-dark"}`} data-testid="referral-count">
              <Users className="w-4 h-4" /> {data.invited} colleague{data.invited === 1 ? "" : "s"} joined through you
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
